/**
 * BottomNavigation component
 * Mobile-first bottom navigation bar with main app sections
 * Only visible on mobile/tablet devices (hidden on desktop)
 */

import { Link, useMatchRoute, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { RootSearchParams } from '@/routes/__root';

interface BottomNavigationProps {
  /** Whether user is an organizer (to show/hide certain nav items) */
  isOrganizer?: boolean;
}

/**
 * BottomNavigation component
 *
 * Features:
 * - Fixed bottom navigation for mobile devices
 * - Active state highlighting based on current route
 * - Touch-friendly 48px height buttons
 * - Icons with labels
 * - Hidden on desktop (lg breakpoint and above)
 * - Shows organizer-only items when applicable
 *
 * @example
 * ```tsx
 * <BottomNavigation isOrganizer={user.isOrganizer} />
 * ```
 */
export function BottomNavigation({ isOrganizer = false }: BottomNavigationProps) {
  const { t } = useTranslation();
  const matchRoute = useMatchRoute();

  // Get root search params to preserve week when navigating (exclude page param)
  const rootSearch = useSearch({ from: '__root__' }) as RootSearchParams;
  const { week, redirect } = rootSearch;
  const navSearch = { week, redirect } as RootSearchParams;

  // Helper to check if route is active
  const isRouteActive = (to: string) => {
    if (to === '/') {
      // For home, only match exact path
      return matchRoute({ to: '/', fuzzy: false }) !== false;
    }
    // For other routes, use fuzzy matching
    return matchRoute({ to, fuzzy: true }) !== false;
  };

  // Define navigation items
  const navItems = [
    {
      to: '/',
      label: t('navigation.home'),
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
      ),
      show: true,
    },
    {
      to: '/availability',
      label: t('navigation.availability'),
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      show: true,
    },
    {
      to: '/weekly-selection',
      label: t('navigation.selection'),
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
          />
        </svg>
      ),
      show: true,
    },
    {
      to: '/menu',
      label: t('navigation.menu'),
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
      show: isOrganizer,
    },
    {
      to: '/meals',
      label: t('navigation.meals'),
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
      show: true,
    },
  ];

  // Filter items based on visibility
  const visibleItems = navItems.filter((item) => item.show);

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40"
      role="navigation"
      aria-label={t('navigation.mainNavigation')}
    >
      <div className="flex justify-around items-center h-16">
        {visibleItems.map((item) => {
          const isActive = isRouteActive(item.to);

          return (
            <Link
              key={item.to}
              to={item.to}
              search={navSearch}
              className={`flex flex-col items-center justify-center flex-1 h-full min-w-0 transition-colors ${
                isActive
                  ? 'text-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
            >
              <div className="flex flex-col items-center justify-center gap-1">
                {item.icon}
                <span className="text-xs font-medium truncate max-w-full px-1">
                  {item.label}
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
